import type { TPartnerProgram, TPromoCode, TFeaturedProgramResponse } from '~/types/partnerProgram';

// Global cache for featured program data
let featuredProgramCache: { program: TPartnerProgram; promoCode: TPromoCode } | null = null;
let featuredProgramPromise: Promise<{ program: TPartnerProgram; promoCode: TPromoCode } | null> | null = null;

export function usePartnerProgram() {
  const { apiQuery } = useApi();

  const fetchProgramBySlug = async (slug: string) => {
    try {
      const response: TFeaturedProgramResponse = await apiQuery(`/public/partner-programs/${slug}`, 'GET');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch program by slug:', error);
      throw error;
    }
  };

  const fetchFeaturedProgram = async (): Promise<{ program: TPartnerProgram; promoCode: TPromoCode } | null> => {
    if (featuredProgramCache) {
      return featuredProgramCache;
    }

    // Return existing promise if fetch is already in progress
    if (featuredProgramPromise) {
      return featuredProgramPromise;
    }

    // Start new fetch and cache the promise
    featuredProgramPromise = (async () => {
      try {
        const response: TFeaturedProgramResponse = await apiQuery('/public/partner-programs/featured', 'GET');

        if (response.data && response.promoCode) {
          featuredProgramCache = {
            program: response.data,
            promoCode: response.promoCode
          };
          return featuredProgramCache;
        }

        return null;
      } catch (error) {
        console.error('Failed to fetch featured program with promo:', error);
        return null;
      } finally {
        featuredProgramPromise = null;
      }
    })();

    return featuredProgramPromise;
  };

  const clearFeaturedProgramCache = () => {
    featuredProgramCache = null;
    featuredProgramPromise = null;
  };

  return {
    fetchFeaturedProgram,
    fetchProgramBySlug,
    clearFeaturedProgramCache,
  };
}